import React, { useCallback, useMemo } from 'react';
import { KnightKey, ContextMenuData, SourceStoneKey } from './types';
import { SWORN_DATA, ALL_BLESSINGS_MAP } from './gameData';
import PlaceholderIcon from './components/ui/PlaceholderIcon';
import PlaceholderPortrait from './components/ui/PlaceholderPortrait';
// import AbilityBar from './components/ui/AbilityBar';
import BuildDetails from './components/panels/BuildDetails';
import CompendiumContent from './components/CompendiumContent';
import MorganaPanel from './components/panels/MorganaPanel';
import MerlinPanel from './components/panels/MerlinPanel';
import NimuePanel from './components/panels/NimuePanel';
import AnvilPanel from './components/panels/AnvilPanel';
import SourcePanel from './components/panels/SourcePanel';
import DefaultSelectionPanel from './components/panels/DefaultSelectionPanel';
import { useSwornBuilderContext } from './contexts/SwornBuilderContext';
import { getIconPath } from './utils/iconPath';
import { capitalize } from './utils/capitalize';
import TutorialOverlay from './components/ui/TutorialOverlay';
import { isEmbeddedAppMode } from './utils/appMode';
import './styles/SwornUILayout.css';

interface SwornUILayoutProps {
  showTutorial: boolean;
  onCloseTutorial: () => void;
}

const SwornUILayout: React.FC<SwornUILayoutProps> = ({ showTutorial, onCloseTutorial }) => {
  const {
    // State
    selectedKnight,
    selectedWeapon,
    toggleBlessing,
    selectedSpell,
    selectedBlessings,
    viewedBlessing,
    currentBuildName,
    setSaveMessage,
    
    // Panel State
    activePanel,
    selectedCompendiumKnight,
    selectedCompendiumFae,
    
    // Panel Modes & Data
    morganaMode,
    selectedMorganaModifiers,
    merlinMode,
    selectedMerlinUpgrades,
    anvilMode,
    selectedAnvilUpgrades,
    nimueSelection: nimueOptions,
    currentNimueModifiers,
    metastones,
    metastoneOptions,
    
    // Actions
    setMorganaMode,
    toggleMorganaModifier,
    setMerlinMode,
    toggleMerlinUpgrade,
    setAnvilMode,
    toggleAnvilUpgrade,
    toggleNimueOption,
    toggleMetastone,
    
    // Navigation / UI Actions
    activatePanel,
    closeAllPanels,
    setSelectedCompendiumKnight,
    setSelectedCompendiumFae,
    handleBlessingListItemClick,
    
    // Helpers
    containerRef,
    
    // Context Menu
    contextMenu,
    openContextMenu,
    closeContextMenu,
    knightKeys,
  } = useSwornBuilderContext();

  const handleContextMenu = (e: React.MouseEvent, target: string, type: ContextMenuData['type'], options?: string[]) => {
    let finalOptions: string[] = [];
    const knight = selectedKnight?.toLowerCase() as KnightKey | undefined;

    if (!knight) {
      if (type === 'knight') {
        finalOptions = knightKeys;
      }
    } else {
      if (type === 'knight') {
        if (options && options.includes('select-list')) {
          finalOptions = knightKeys.filter(k => k.toLowerCase() !== knight);
        } else if (options && options.includes('select-compendium')) {
          finalOptions = [target];
        } else {
          finalOptions = ['view'];
        }
      } else if (type === 'weapon') {
        finalOptions = Object.keys(SWORN_DATA.classes[knight]?.weapons || {});
      } else if (type === 'spell') {
        finalOptions = Object.keys(SWORN_DATA.classes[knight]?.spells || {});
      } else if (type === 'blessing') {
        finalOptions = ['view', 'remove'];
      } else if (type === 'build') {
        finalOptions = ['Rename', 'Save', 'Load', 'New'];
      }
    }

    if (finalOptions.length > 0) {
      openContextMenu(e, target, type, finalOptions);
    }
  };

  const onKnightClick = useCallback((e: React.MouseEvent) => {
      if (e && e.clientX) {
          e.preventDefault();
          e.stopPropagation();
          handleContextMenu(e, 'knight-pane', 'knight', ['select-list']);
      }
  }, [handleContextMenu]);

  const onLoadoutClick = useCallback((e: React.MouseEvent) => {
      if (e && e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          const id = e.currentTarget.id;
          if (id === 'weapon-pane') {
            handleContextMenu(e, selectedWeapon || 'weapon', 'weapon');
          } else if (id === 'spell-pane') {
            handleContextMenu(e, selectedSpell || 'spell', 'spell');
          }
      }
  }, [handleContextMenu, selectedWeapon, selectedSpell]);

  const handleBlessingContextAction = (blessingName: string, action: string) => {
      if (action === 'add' || action === 'remove') {
          toggleBlessing(blessingName);
      }
  };

  // --- Memoized Click Handlers for UI Elements ---

  const handleAnvilClick = useCallback(() => {
    if (contextMenu) return closeContextMenu();
    activatePanel('anvil');
  }, [contextMenu, closeContextMenu, activatePanel]);

  const handleMerlinClick = useCallback(() => {
    if (contextMenu) return closeContextMenu();
    activatePanel('merlin');
  }, [contextMenu, closeContextMenu, activatePanel]);

  const handleMorganaClick = useCallback(() => {
    if (contextMenu) return closeContextMenu();
    activatePanel('morgana');
  }, [contextMenu, closeContextMenu, activatePanel]);

  const handleNimueClick = useCallback(() => {
    if (contextMenu) return closeContextMenu();
    activatePanel('nimue');
  }, [contextMenu, closeContextMenu, activatePanel]);

  const handleSourceClick = useCallback(() => {
    if (contextMenu) return closeContextMenu();
    activatePanel('source');
  }, [contextMenu, closeContextMenu, activatePanel]);

  // Stubs for future implementation
  const onBuildDetailBannerClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleContextMenu(e, 'build', 'build', ['Rename', 'Save As', 'Save', 'Load', 'New']);
  }, [handleContextMenu]);
  const onBlessingNameBannerClick = useCallback(() => {}, []);
  const onSelectedBlessingIconClick = useCallback(() => {}, []);
  const onBuildBlessingListClick = useCallback(() => {}, []);

  const knightImageUrl = selectedKnight ? getIconPath('knight', selectedKnight, { framed: true }) : '';
  const viewedBlessingData = viewedBlessing ? ALL_BLESSINGS_MAP.get(viewedBlessing) : null;

  const isSidePanelActive = activePanel !== null || selectedCompendiumKnight || selectedCompendiumFae;

  const renderRightPanel = () => {
    if (activePanel === 'morgana') {
      return (
        <MorganaPanel 
          mode={morganaMode}
          setMode={setMorganaMode}
          selectedModifiers={selectedMorganaModifiers}
          toggleModifier={toggleMorganaModifier}
        />
      );
    }

    if (activePanel === 'merlin') {
      return (
        <MerlinPanel 
          mode={merlinMode}
          setMode={setMerlinMode}
          selectedUpgrades={selectedMerlinUpgrades}
          toggleUpgrade={toggleMerlinUpgrade}
        />
      );
    }

    if (activePanel === 'anvil') {
      return (
        <AnvilPanel 
          mode={anvilMode}
          setMode={setAnvilMode}
          selectedUpgrades={selectedAnvilUpgrades}
          toggleUpgrade={toggleAnvilUpgrade}
          selectedKnight={selectedKnight}
          selectedWeapon={selectedWeapon}
          selectedSpell={selectedSpell}
        />
      );
    }

    if (activePanel === 'nimue') {
      if (!currentNimueModifiers) {
        return <div className="p-4 text-center text-red-500">Please select a knight and weapon first.</div>;
      }
      return (
        <NimuePanel 
          options={nimueOptions}
          currentModifiers={currentNimueModifiers}
          toggleOption={toggleNimueOption}
          selectedWeapon={selectedWeapon}
        />
      );
    }

    if (activePanel === 'source') {
      return (
        <SourcePanel 
          metastones={metastones}
          metastoneOptions={metastoneOptions as Record<SourceStoneKey, 'A' | 'B'>}
          toggleMetastone={toggleMetastone}
        />
      );
    }

    if (selectedCompendiumKnight || selectedCompendiumFae) {
      return (
        <div id="compendium-panel">
          <div style={{ overflow: 'auto', height: '100%', backgroundColor: 'transparent' }}>
            <CompendiumContent
              selectedKnight={selectedCompendiumKnight}
              selectedFae={selectedCompendiumFae}
              selectedBlessings={selectedBlessings}
              toggleBlessing={(name) => handleBlessingContextAction(name, 'add')}
              onClose={() => {
                setSelectedCompendiumKnight(null);
                setSelectedCompendiumFae(null);
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <DefaultSelectionPanel 
        selectedKnight={selectedKnight}
        onKnightSelect={(knight) => contextMenu ? closeContextMenu() : setSelectedCompendiumKnight(knight)}
        onFaeSelect={(fae) => contextMenu ? closeContextMenu() : setSelectedCompendiumFae(fae)}
        getIconPath={getIconPath}
        contextMenu={contextMenu}
        closeContextMenu={closeContextMenu}
        handleContextMenu={handleContextMenu}
      />
    );
  };

  const backgrounds = SWORN_DATA.ui_assets?.backgrounds || {};
  const getBackgroundImage = () => {
    if (activePanel === 'morgana') return `url(${backgrounds.morgana})`;
    if (activePanel === 'merlin') return `url(${backgrounds.merlin})`;
    if (activePanel === 'anvil') return `url(${backgrounds.anvil})`;
    if (activePanel === 'source') return `url(${backgrounds.source})`;
    if (activePanel === 'nimue') return `url(${backgrounds.nimue})`;
    if (selectedCompendiumKnight || selectedCompendiumFae) return `url(${backgrounds.compendium})`;
    return `url(${backgrounds.default})`;
  };

  const panelIcons = SWORN_DATA.ui_assets?.panels;
  const shouldBypassLandscapeLock = useMemo(() => isEmbeddedAppMode(), []);

  return (
    <div className={`w-full max-w-[95%] md:max-w-[80%] mx-auto relative ${shouldBypassLandscapeLock ? 'app-mode-bypass' : ''}`}>
      {/* Landscape Warning Overlay */}
      {!shouldBypassLandscapeLock && (
        <div className="landscape-warning">
          <div className="rotate-icon">⟳</div>
          <p>Please rotate your device to landscape mode.</p>
        </div>
      )}

      {/* <AbilityBar selectedBlessings={selectedBlessings} /> */}
      <div
        ref={containerRef}
        className="main-layout-container"
        style={{
          backgroundImage: getBackgroundImage(),
        }}
      >
        {/* Left Column */}
        <div 
          id="char-preview-pane" 
          className={`interactive-element ${contextMenu ? 'no-hover' : ''}`} 
          onClick={onKnightClick} 
          onContextMenu={(e) => { e.preventDefault(); handleContextMenu(e, 'knight-pane', 'knight', ['select-list']); }}
        >
          <PlaceholderPortrait label={selectedKnight || "Select Knight"} imageUrl={knightImageUrl} />
        </div>
        <div 
          id="knight-class-name" 
          className={`interactive-element ${contextMenu ? 'no-hover' : ''}`} 
          onClick={onKnightClick} 
          onContextMenu={(e) => { e.preventDefault(); handleContextMenu(e, 'knight-pane', 'knight', ['select-list']); }}
        >
          <div className="text-sworn-gold text-lg font-semibold text-center">
            {selectedKnight ? capitalize(selectedKnight) : 'Select Knight'}
          </div>
        </div>

        <div id="loadout-group">
          <div
            id="weapon-pane"
            className={`interactive-element ${contextMenu ? 'no-hover' : ''}`}
            onClick={onLoadoutClick}
            onContextMenu={(e) => {
              e.preventDefault();
              handleContextMenu(e, selectedWeapon || 'weapon', 'weapon');
            }}>
            <PlaceholderIcon className="w-full h-full" label={selectedWeapon ? selectedWeapon.split('_').join(' ') : 'Weapon'} imageUrl={selectedWeapon ? getIconPath('weapon', selectedWeapon, { knight: selectedKnight || undefined }) : ''} />
          </div>
          <div 
            id="spell-pane" 
            className={`interactive-element ${contextMenu ? 'no-hover' : ''}`} 
            onClick={onLoadoutClick}
            onContextMenu={(e) => { e.preventDefault(); handleContextMenu(e, selectedSpell || 'spell', 'spell'); }}
          >
            <PlaceholderIcon className="w-full h-full" label={selectedSpell ? selectedSpell.split('_').join(' ') : 'Spell'} imageUrl={selectedSpell ? getIconPath('spell', selectedSpell, { knight: selectedKnight || undefined }) : ''} />
          </div>
        </div>

        {/* Center Column */}
        <BuildDetails />

        {/* Modifier Slots */}
        <div 
          id="modifier-slot-1" 
          className={`interactive-element ${contextMenu ? 'no-hover pointer-events-none' : 'pointer-events-auto'}`}
          onClick={handleAnvilClick}
        >
          <PlaceholderIcon label="Sword in Stone" imageUrl={panelIcons?.anvil_icon} className="w-full h-full" />
        </div>
        
        <div id="modifier-slot-2">
          <div 
            id="merlin-pane" 
            className={`interactive-element ${contextMenu ? 'no-hover pointer-events-none' : 'pointer-events-auto'}`} 
            onClick={handleMerlinClick}
          >
            <PlaceholderIcon label="Merlin" imageUrl={panelIcons?.merlin_icon} className="w-full h-full" imageClassName="object-cover" />
          </div>
          <div 
            id="morgana-pane" 
            className={`interactive-element ${contextMenu ? 'no-hover pointer-events-none' : 'pointer-events-auto'}`} 
            onClick={handleMorganaClick}
          >
            <PlaceholderIcon label="Morgana" imageUrl={panelIcons?.morgana_icon} className="w-full h-full" imageClassName="object-cover" />
          </div>
        </div>

        <div id="modifier-slot-3">
          <div 
            id="nimune-forge-pane" 
            className={`interactive-element ${contextMenu ? 'no-hover pointer-events-none' : 'pointer-events-auto'}`} 
            onClick={handleNimueClick}
          >
            <PlaceholderIcon label="Nimue" imageUrl={panelIcons?.nimue_icon} className="w-full h-full" imageClassName="object-cover" />
          </div>
          <div 
            id="the-source-pane" 
            className={`interactive-element ${contextMenu ? 'no-hover pointer-events-none' : 'pointer-events-auto'}`} 
            onClick={handleSourceClick}
          >
            <PlaceholderIcon label="Source" imageUrl={panelIcons?.source_icon} className="w-full h-full" imageClassName="object-cover" />
          </div>
        </div>

        {/* --- Right Panel --- */}
        {renderRightPanel()}

        {/* Unified Close Button */}
        {isSidePanelActive && (
          <button
            onClick={closeAllPanels}
            id="close-side-panel-button"
            className={activePanel === 'morgana' ? 'morgana-close' : ''}
          >
            ✕
          </button>
        )}

        {showTutorial && <TutorialOverlay onClose={onCloseTutorial} />}
      </div>
    </div>
  );
};

export default SwornUILayout;
